import fetch from 'isomorphic-fetch';

import { stateHelperObj, shouldUpdate, filterValuesInitState } from '../reducers/filter';
import { getFromStorage, hasLocalData } from '../utils/localStorageHelper';

import * as stallionActions from './stallion';
import * as filterActions from './filter';
import { shuffleArray } from '../utils/dataHelper';

const apiURL = '/bloodstock/stallion-book/filter';
const stallionNamesUrl = '/bloodstock/stallion-book/names';
const mostFeaturedUrl = '/bloodstock/stallion-book/most-featured';


/**
 * getApiUrl function
 *
 * @return string - Resulted Url to make api request
 *
 * @param filterObj - Object
 *
 * Function iterates over filter object and appends corresponding keys and values
 * as request parameters to globally declared apiUrl. Fields that are used to
 * help manage state are taken from stateHelperObj and filtered out
 * **/
export function getApiUrl(filterObj, type = 'entries') {
    let queryParams = '';
    const paramsObj = Object.assign({}, filterObj);
    const excludedKeys = Object.keys(stateHelperObj);

    if (type === 'names') {
        return `${stallionNamesUrl}?type=${filterObj.type}`;
    }

    Object.keys(paramsObj)
        .filter(key => paramsObj[key] && excludedKeys.indexOf(key) < 0)
        .forEach((key) => {
            let isArrayParam = false;

            if (key !== 'studFarm') {
                paramsObj[key] = paramsObj[key].replace(/\.|'|"|&| /g, '');
            }

            if (paramsObj[key].split(',').length > 1) {
                isArrayParam = true;
                paramsObj[key] = paramsObj[key].split(',').join(`&${key}[]=`);
            }

            if (key === 'studFee') {
                const [minPrice, maxPrice] = paramsObj[key].split('/');

                if (minPrice) {
                    queryParams = `${queryParams}&minPrice=${minPrice}`;
                }

                if (maxPrice) {
                    queryParams = `${queryParams}&maxPrice=${maxPrice}`;
                }
                return;
            }


            queryParams = queryParams
                ? `${queryParams}&${key}${isArrayParam ? '[]' : ''}=${paramsObj[key]}`
                : `${key}=${paramsObj[key]}`;
        });

    return queryParams ? `${apiURL}?${queryParams}` : apiURL;
}
/**
 * requestFailure function
 *
 * @return Object, data request failure action
 *
 * @param error - Object, error object received with response
 * **/
export function requestFailure(error) {
    return {
        type: stallionActions.DATA_FAILURE,
        error
    };
}
/**
 * requestEntries function
 *
 * @return Object, corresponding data request action
 *
 * @param dataType - string, determines which type of data is being requested
 * @param initial - boolean, initial action flag, defaults to false
 * @param isSwitcherAction - boolean, switcher action flag, defaults to false
 *
 * Switches over data types to determine which action should be dispatched.
 * This is necessary as, based on type of data that is being requested,
 * different logic will be triggered.
 * **/
export function requestEntries(dataType, initial = false, isSwitcherAction = false) {
    switch (dataType) {
        case 'stallion':
            return {
                type: stallionActions.DATA_REQUEST,
                initial,
                isSwitcherAction
            };
        default:
            return {
                type: stallionActions.RAW_DATA_REQUEST
            };
    }
}
/**
 * receiveEntries function
 *
 * @return Object, action to dispatch
 *
 * @param dataType - string, data type that has been received
 * @param filterType - string, current state filter type
 * @param payload - json, data that has been received
 * @param isSwitcherAction - boolean, switcher action flag
 * @param initial - boolean, initial action flag
 *
 * Returns data request success action with corresponding action type (based on dataType param),
 * payload (data param), filter type (filterType param) and flags (initial, isSwitcherAction)
 * **/
export function receiveEntries(dataType = 'names',
                        filterType = 'active',
                        payload = [],
                        isSwitcherAction = false,
                        initial = false) {
    let type;

    switch (dataType) {
        case 'stallion':
            type = stallionActions.DATA_SUCCESS;
            break;
        default:
            type = stallionActions.RAW_DATA_SUCCESS;
            break;
    }

    return {
        type,
        payload,
        filterType,
        isSwitcherAction,
        initial
    };
}
/**
 * filterStatePristine function
 *
 * @return Object, action to dispatch, set filter state as pristine
 *
 * @param initial - boolean, initial action flag
 * **/
export function filterStatePristine(initial = false) {
    return {
        type: filterActions.STATE_PRISTINE,
        initial
    };
}
/**
 * fetchLocal function
 *
 * @param currentFilter - object, current filter state.
 * @param prefix - string, defaults to "type". Used to determine a type of data that
 *                 is being pulled from localStorage and form correct payload structure and
 *                 correct action type to dispatch.
 * @param dispatch - function, used to dispatch action
 * @param initial - boolean, used to mark initial data requests.
 *
 * Get required data from localStorage and dispatch corresponding action.
 * **/
export function fetchLocal(currentFilter, prefix = 'type', dispatch, initial) {
    let localData = getFromStorage(`${prefix}-${currentFilter.type}`);
    let dataType = 'names';

    if (prefix === 'results') {
        localData = {
            stallion_book: localData
        };
        dataType = 'stallion';
    }

    dispatch(receiveEntries(dataType, currentFilter.type, localData, false, initial));
}
/**
 * fetchFilterData function
 * @return function, action dispatcher
 *
 * Filter requires additional data (stallion names), it is being stored in localStorage,
 * so it is loaded only once. Function checks for data presence in localStorage,
 * lookup is performed by filter type key (e.g. "type-active"). If data is present -
 * it will be taken from localStorage, otherwise it will be fetched,
 * using specific Url (stallionNamesUrl) with current filter type as request param.
 * **/
export function fetchFilterData() {
    return (dispatch, getState) => {
        const currentFilter = getState().filter;
        const inLocalStorage = hasLocalData(`type-${currentFilter.type}`);

        if (inLocalStorage) {
            fetchLocal(currentFilter, 'type', dispatch);
        } else {
            const apiUrl = getApiUrl(currentFilter, 'names');
            dispatch(requestEntries('names'));
            fetch(apiUrl)
                .then(response => response.json())
                .then(json => dispatch(receiveEntries('names', currentFilter.type, json, true)))
                .catch(error => dispatch(requestFailure(error)));
        }
    };
}
/**
 * fetchStallion function
 *
 * @return function, action dispatcher
 *
 * @param initial - boolean, Flag that marks initial data requests.
 * @param isSwitcherAction - boolean, Flag that marks stallion requests
 *                                    triggered by switcher actions
 *
 * Function is used to fetch stallions. Stallion search results for default filter state is stored
 * in localStorage for each stallion type group by a specific key (e.g. "results-active").
 * Initial flag will be received by corresponding reducer to
 * determine whether results table should be shown,
 * as we don't show results on first load even though it is available.
 * isSwitcherAction flag will also be passed to reducer to
 * help determine if results table should be updated,
 * as while switching stallion types we should not update results table until user pressed "Search".
 * If data is present in localStorage and filter is in default state (currentFilter.shouldUpdate),
 * it will be pulled from localStorage.
 * **/
export function fetchStallion(initial = false, isSwitcherAction = false) {
    return (dispatch, getState) => {
        const filterKeysList = Object.keys(filterValuesInitState);

        const currentFilter = filterKeysList.reduce(
          (obj, filterKey) =>
            Object.assign(
              obj,
              { [filterKey]: getState().filter[filterKey] },
              {}
            ),
          filterValuesInitState
        );

        const inLocalStorage = hasLocalData(`results-${currentFilter.type}`);
        const isDefaultFilter = !shouldUpdate(currentFilter);

        if (inLocalStorage && isDefaultFilter) {
            fetchLocal(currentFilter, 'results', dispatch, initial);
            dispatch(filterStatePristine(initial));
        } else {
            const defaultFilter = Object.assign({}, filterValuesInitState, { type: currentFilter.type });
            const apiUrl = getApiUrl(isSwitcherAction ? defaultFilter : currentFilter);

            dispatch(requestEntries('stallion', initial, isSwitcherAction));

            fetch(apiUrl)
                .then(response => response.json())
                .then(json =>
                        dispatch(receiveEntries('stallion',
                            currentFilter.type, json,
                            isSwitcherAction,
                            initial)
                        )
                )
                .then(() => {
                    if (!isSwitcherAction) {
                        return dispatch(filterStatePristine(initial));
                    }
                    return undefined;
                })
                .catch(error => dispatch(requestFailure(error)));
        }
    };
}

export function mostFeaturedFailure(error) {
    return {
        type: stallionActions.MOST_FEATURED_FAILURE,
        error
    };
}

export function mostFeaturedRequest() {
    return {
        type: stallionActions.MOST_FEATURED_REQUEST,
    };
}

export function mostFeaturedRecieved(data) {
    return {
        type: stallionActions.MOST_FEATURED_SUCCESS,
        data
    };
}
export function fetchMostFeatured() {
    return (dispatch) => {
        dispatch(mostFeaturedRequest());

        fetch(mostFeaturedUrl)
                .then(response => response.json())
                .then(json => {
                    dispatch(mostFeaturedRecieved(shuffleArray(json.mostFeatured, new Date().getTime())));
                })
                .catch(error => dispatch(mostFeaturedFailure(error)));
    };
}


/**
 * toggleFilter function
 *
 * @return function, toggle filter action dispatcher
 *
 * @param type - string, action type of corresponding Select
 * @param value - string, value of select
 * **/
export function toggleFilter(type, value) {
    return (dispatch) => {
        dispatch({
            type,
            value
        });
    };
}
/**
 * updateFilterType function
 *
 * @return function, update filter action dispatcher.
 *
 * @param value - string, selected value
 *
 * Updates state of filter with selected type
 * **/
export function updateFilterType(value) {
    return (dispatch) => {
        dispatch({
            type: filterActions.TYPE,
            value
        });
    };
}
/**
 * updateSireLineFlag function
 *
 * @return function, update sire line flag action dispatcher
 *
 * @param value - string, selected value
 *
 * Updates state of filter with selected sire line flag
 * **/
export function updateSireLineFlag(value) {
    return (dispatch) => {
        dispatch({
            type: filterActions.SIRE_LINE_FLAG,
            value
        });
    };
}
/**
 * dropFilter function
 *
 * @return function, drop filter action dispatcher
 *
 * Restores filter to default state
 * **/
export function dropFilter() {
    return (dispatch) => {
        dispatch({
            type: filterActions.CLEAR
        });
    };
}
/**
 * toggleAdvancedSection function
 *
 * @return function, displays/hide advanced filter section
 *
 * Updates state of filter with advanced section visibility flag
 * **/
export function setAdvancedSectionVisibility(value) {
    return (dispatch) => {
        dispatch({
            type: filterActions.SET_ADVANCED_SECTION,
            value
        });
    };
}


/**
 * toggleAdvancedSection function
 *
 * @return function, displays/hide filter popover for mobiles
 *
 * Updates state of filter with popover visibility flag
 * **/
export function setFilterPopoverVisibility(value) {
    return (dispatch) => {
        dispatch({
            type: filterActions.SET_FILTER_POPOVER,
            value
        });
    };
}



// WEBPACK FOOTER //
// ./src/client/actions/index.js